/**
 * This component will be used for PDP to display the add to bag button and add to favorite button, showing an error message if anything goes wrong when adding a product to the bag.
 *
 * @module views/components/FulfillmentButton
 * @memberof -Common
 */
import './FulfillmentButton.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Button from '@ulta/modules/Button/Button';

import * as utils from './FulfillmentButton';

/**
 * Represents a FulfillmentButton component
 *
 * @method
 * @param {FulfillmentButtonProps} props - React properties passed from composition
 * @returns FulfillmentButton
 */
export const FulfillmentButton = function( props ){
  const {
    addToBagAction,
    bookmarkAction,
    bookmarked,
    buttonAction,
    commerceId,
    fulfillmentType,
    id,
    inBagLabel,
    incrementBagAccessibilityLabel,
    invokeMutation,
    isDisabled,
    isSecondaryState,
    maxQuantity,
    productId,
    brandName,
    quantity,
    removeBookmarkAction,
    removeFromBagAction,
    skuId,
    state,
    successLabel,
    invokeAction,
    isSubscribed,
    subscriptionFrequency,
    updateBagAction,
    updateSuccessLabel,
    showUpdateBag,
    setIsFulfillmentChanged,
    isWebStickyATBButton,
    stickyAddToBagAccessibilityLabel,
    wishListAction,
    isDisabledForNotify,
    isMyListOverlayOpen,
    removeWishListAction,
    loading
  } = props;

  return (
    <div className='FulfillmentButton'>
      <div className='FulfillmentButton__Container'>
        { !isSecondaryState && !isDisabledForNotify &&
        <div className={ classNames( 'FulfillmentButton__AddToBag', {
          'FulfillmentButton__AddToBag--disabled': isDisabled
        } ) }
        >
          <AsyncComponent
            isWebStickyATBButton={ isWebStickyATBButton }
            stickyAddToBagAccessibilityLabel={ stickyAddToBagAccessibilityLabel }
            addToBagAction={ buttonAction || addToBagAction }
            commerceId={ commerceId }
            default={ { ...props, disabled: isDisabled, secondary: isSecondaryState } }
            fulfillmentType={ fulfillmentType }
            id={ id }
            inBagLabel={ inBagLabel }
            incrementBagAccessibilityLabel={ incrementBagAccessibilityLabel }
            invokeMutation={ invokeMutation }
            key='AddToBagButton'
            maxQuantity={ maxQuantity }
            moduleName='AddToBagButton'
            productId={ productId }
            quantity={ quantity }
            removeFromBagAction={ buttonAction || removeFromBagAction }
            skuId={ skuId }
            state={ state }
            successLabel={ successLabel }
            isSubscribed={ isSubscribed }
            subscriptionFrequency={ subscriptionFrequency }
            updateBagAction={ updateBagAction }
            updateSuccessLabel={ updateSuccessLabel }
            showUpdateBag={ showUpdateBag }
            setIsFulfillmentChanged={ setIsFulfillmentChanged }
          />
        </div>
        }
        { isSecondaryState &&
        <div className='FulfillmentButton__SecondaryButton'>
          <Button
            className='CheckAnotherStore--Button'
            label={ buttonAction?.label }
            secondary
            onClick={ () => utils.handleButtonAction( { buttonAction, productId, skuId, fulfillmentType }, { invokeAction } ) }
          />
        </div>
        }
        { isDisabledForNotify &&
          <div className='FulfillmentButton__SecondaryButton'>
            <Button
              label={ buttonAction?.label }
              ariaLabel={ buttonAction?.label }
              fullWidth={ true }
              size='compact'
              disabled
            />
          </div>
        }
        <div
          className='FulfillmentButton__Favorite'
        >
          <AsyncComponent
            id={ `ProductBookmark-${productId}-${skuId}` }
            bookmarkAction={ wishListAction || bookmarkAction }
            bookmarked={ bookmarked }
            commerceId={ commerceId }
            key='ProductBookmark'
            moduleName='ProductBookmark'
            productActionsProps={ props }
            productId={ productId }
            brandName={ brandName }
            quantity={ quantity }
            removeBookmarkAction={ removeWishListAction }
            skuId={ skuId }
            state={ state }
            invokeMutation={ invokeMutation }
            isMyListOverlayOpen={ isMyListOverlayOpen }
            fulfillmentType={ fulfillmentType }
            loading={ loading }
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Handles the button action by invoking it with the required parameters.
 *
 * @param {Object} data - contains the action and parameters.
 * @param {Object} data.buttonAction - contian the button action.
 * @param {string} data.productId - product Id.
 * @param {string} data.skuId -  product SKU Id.
 * @param {string} data.fulfillmentType -  fulfillmentType.
 * @param {Object} methods - contains utility functions.
 * @param {Function} methods.invokeAction - A function to invoke the actions.
 */
export const handleButtonAction = ( data, methods ) => {
  const { buttonAction, productId, skuId, fulfillmentType } = handleEmptyObjects( data );
  const { invokeAction } = methods;
  if( !buttonAction ){
    return;
  }
  invokeAction( {
    ...buttonAction,
    variables : {
      moduleParams: {
        productId,
        skuId,
        fulfillmentType
      }
    }
  } );
};

/**
 * Property type definitions
 * @typedef FulfillmentButtonProps
 * @type {object}
 * @property {object} addToBagAction - Sets label and graphql
 * @property {object} updateBagAction - Sets label and graphql
 * @property {object} removeFromBagAction - Sets label and graphql
 * @property {string} successLabel - Sets success Label
 * @property {string} addToBagErrorLabel - Sets error Label
 * @property {string} inBagLabel - Sets in bag Label
 * @property {string} maxQuantity - Sets max quantity
 * @property {boolean} bookmarked - True/False value for the bookmarked
 * @property {object} bookmarkAction - Label to be displayed
 * @property {object} removeBookmarkAction - Label to remove from bag
 * @property {string} commerceId - Id that is used to update the bag
 * @property {string} fulfillmentType - fulfillmentType
 * @property {function} invokeAction - invokeAction- calls DXL
 * @property {boolean} isSubscribed - Is Subscribed
 * @property {function} setIsFulfillmentChanged - Function to reset the fulfillment change flag
 * @property {string} subscriptionFrequency - Subscription Frequency
 * @property {boolean} isWebStickyATBButton - shows sticky ATB btn
 * @property {string} stickyAddToBagAccessibilityLabel - sets ada label for sticky atb
 * * @property {boolean} isDisabledForNotify - to check if notify me is disabled
 */
export const propTypes = {
  addToBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  updateBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeFromBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  successLabel: PropTypes.string,
  addToBagErrorLabel: PropTypes.string,
  inBagLabel: PropTypes.string,
  maxQuantity: PropTypes.number,
  bookmarked: PropTypes.bool,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  commerceId: PropTypes.string,
  fulfillmentType: PropTypes.string,
  invokeAction: PropTypes.func,
  isSubscribed: PropTypes.bool,
  setIsFulfillmentChanged: PropTypes.func,
  subscriptionFrequency: PropTypes.string,
  isWebStickyATBButton: PropTypes.bool,
  stickyAddToBagAccessibilityLabel: PropTypes.string,
  isDisabledForNotify: PropTypes.string
};

FulfillmentButton.propTypes = propTypes;

export default FulfillmentButton;
